import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getReadyMatches,
  startTournamentMatchWithGame,
  resetMatchToReady,
} from '../services/tournamentsV2';
import { getClubTableDevices, assignGameToTable } from '../services/tableDevices';
import { subscribeToTournamentMatches, unsubscribe } from '../services/realtime';
import { useAuth } from '../contexts/AuthContext';
import { ReadyMatch, TableDeviceWithDetails } from '../types';
import './TournamentMatchManagementV2.css';

interface TournamentMatchManagementV2Props {
  tournamentId: number;
  tournamentName: string;
  clubId: number; // ← NEU: Für Tisch-Zugriff
  isOpen: boolean;
  onClose: () => void;
  matchRefreshKey?: number;
}

const TournamentMatchManagementV2: React.FC<TournamentMatchManagementV2Props> = ({
  tournamentId,
  tournamentName,
  clubId,
  isOpen,
  onClose,
  matchRefreshKey,
}) => {
  const { t } = useTranslation();
  const { player } = useAuth();
  const [matches, setMatches] = useState<ReadyMatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [startingMatch, setStartingMatch] = useState<string | null>(null);
  const [resettingMatch, setResettingMatch] = useState<string | null>(null);
  
  // ── Table Assignment State ──
  const [availableTables, setAvailableTables] = useState<TableDeviceWithDetails[]>([]);
  const [showTableModal, setShowTableModal] = useState(false);
  const [selectedMatchForTable, setSelectedMatchForTable] = useState<string | null>(null);
  const [selectedTableId, setSelectedTableId] = useState<number | null>(null);
  const [assigningTable, setAssigningTable] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadMatches();
      loadAvailableTables();
    }
  }, [isOpen, tournamentId, matchRefreshKey]);

  // Realtime subscription for tournament match updates
  useEffect(() => {
    if (!isOpen || !tournamentId) return;

    console.log(`Setting up realtime subscription for tournament ${tournamentId} matches`);
    
    const channel = subscribeToTournamentMatches(
      tournamentId,
      (payload) => {
        console.log('Realtime tournament match update received in match management:', payload);
        // Reload matches when any match changes
        loadMatches();
      }
    );

    // Cleanup subscription on unmount or when modal closes
    return () => {
      console.log(`Cleaning up realtime subscription for tournament ${tournamentId} matches`);
      unsubscribe(channel);
    };
  }, [isOpen, tournamentId]);

  const loadMatches = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getReadyMatches(tournamentId);
      setMatches(data);
    } catch (err) {
      console.error('Error loading matches:', err);
      setError(err instanceof Error ? err.message : 'Failed to load matches');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableTables = async () => {
    try {
      const tables = await getClubTableDevices(clubId);
      // Filter: nur approved tables ohne aktuelles Spiel
      const available = tables.filter(
        t => t.status === 'approved' && !t.current_game_id
      );
      setAvailableTables(available);
      return available; // ← Rückgabe für direkten Zugriff
    } catch (err) {
      console.error('Error loading tables:', err);
      return [];
    }
  };

  const handleStartMatch = async (matchId: string) => {
    try {
      setStartingMatch(matchId);
      
      // Tische neu laden und DIREKT verwenden (nicht über State!)
      const currentTables = await loadAvailableTables();
      
      console.log('🎯 DEBUG: Available tables:', currentTables.length);
      console.log('🎯 DEBUG: Tables:', currentTables);
      
      const { gameId } = await startTournamentMatchWithGame(matchId);
      
      // Wenn Tische verfügbar sind, frage nach Tisch-Zuweisung
      if (currentTables.length > 0) {
        console.log('✅ Opening table modal');
        setSelectedMatchForTable(matchId);
        setShowTableModal(true);
        // Game ID wird für spätere Zuweisung gespeichert
        (window as any).__lastCreatedGameId = gameId;
      } else {
        console.log('❌ No tables available, showing alert');
        // Keine Tische verfügbar - nur Erfolgsmeldung
        alert(`Match started! Game ID: ${gameId}\n\nThe players can now play this game.`);
      }
      
      // Refresh the matches list to show updated status
      loadMatches();
    } catch (err) {
      console.error('Error starting match:', err);
      alert(err instanceof Error ? err.message : 'Failed to start match');
    } finally {
      setStartingMatch(null);
    }
  };

  const handleAssignTable = async () => {
    if (!selectedTableId || !player) return;
    
    const gameId = (window as any).__lastCreatedGameId;
    if (!gameId) {
      alert('Game ID not found');
      return;
    }

    try {
      setAssigningTable(true);
      await assignGameToTable(selectedTableId, gameId, player.id);
      
      alert(`Table assigned successfully!\n\nThe game will now appear on the table display.`);
      
      // Reset state
      setShowTableModal(false);
      setSelectedMatchForTable(null);
      setSelectedTableId(null);
      delete (window as any).__lastCreatedGameId;
      
      // Refresh tables list
      loadAvailableTables();
      
      // Close main modal
      onClose();
    } catch (err) {
      console.error('Error assigning table:', err);
      alert(err instanceof Error ? err.message : 'Failed to assign table');
    } finally {
      setAssigningTable(false);
    }
  };

  const handleSkipTableAssignment = () => {
    setShowTableModal(false);
    setSelectedMatchForTable(null);
    setSelectedTableId(null);
    delete (window as any).__lastCreatedGameId;
    onClose();
  };

  const handleResetMatch = async (matchId: string) => {
    if (!window.confirm(t('tournaments.matches.confirmReset') || 'Reset this match back to ready state? This will allow restarting the match.')) {
      return;
    }

    try {
      setResettingMatch(matchId);
      await resetMatchToReady(matchId);
      
      alert(t('tournaments.matches.resetSuccess') || 'Match reset successfully! You can now start it again.');
      loadMatches(); // Refresh list
    } catch (err) {
      console.error('Error resetting match:', err);
      alert(err instanceof Error ? err.message : 'Failed to reset match');
    } finally {
      setResettingMatch(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content match-management-modal">
        <div className="modal-header">
          <h3>{t('tournaments.matches.title')} - {tournamentName}</h3>
          <button onClick={onClose} className="close-button">&times;</button>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}

          {loading ? (
            <div className="loading">{t('common.loading')}</div>
          ) : matches.length === 0 ? (
            <div className="empty-state">
              <p>{t('tournaments.matches.noReadyMatches')}</p>
              <p className="help-text">{t('tournaments.matches.noReadyMatchesHelp')}</p>
            </div>
          ) : (
            <div className="matches-list">
              {matches.map(match => {
                const isPending = match.state === 'pending';
                const isReady = match.state === 'ready';
                const isWalkover = match.state === 'void';
                const isInProgress = match.state === 'in_progress';
                const isCompleted = match.state === 'completed';
                const winnerName = match.player_1_name !== 'TBD' ? match.player_1_name : match.player_2_name;
                
                // DEBUG
                console.log(`🔍 Match ${match.match_number}: state=${match.state}, isInProgress=${isInProgress}`);
                
                // Determine card styling
                let cardClass = 'match-card';
                if (isWalkover) cardClass += ' walkover';
                if (isPending) cardClass += ' pending';
                if (isCompleted) cardClass += ' completed';
                if (isInProgress) cardClass += ' in-progress';
                
                return (
                  <div key={match.match_id} className={cardClass}>
                    <div className="match-info">
                      <div className="match-header">
                        <span className="match-round">
                          {t('tournaments.matches.round')} {match.round_number}
                        </span>
                        <span className="match-number">
                          {t('tournaments.matches.match')} {match.match_number}
                        </span>
                        {/* State Badge */}
                        {isPending && (
                          <span className="state-badge pending">
                            {t('tournaments.matches.state.pending')}
                          </span>
                        )}
                        {isReady && (
                          <span className="state-badge ready">
                            {t('tournaments.matches.state.ready')}
                          </span>
                        )}
                        {isWalkover && (
                          <span className="state-badge walkover">
                            {t('tournaments.matches.walkover')}
                          </span>
                        )}
                        {isInProgress && (
                          <span className="state-badge in-progress">
                            {t('tournaments.matches.state.in_progress')}
                          </span>
                        )}
                        {isCompleted && (
                          <span className="state-badge completed">
                            {t('tournaments.matches.state.completed')}
                          </span>
                        )}
                      </div>
                      <div className="match-players">
                        {isWalkover ? (
                          <>
                            <div className="walkover-banner">
                              🏆 WALKOVER - {t('tournaments.matches.walkover').toUpperCase()}
                            </div>
                            <div className="walkover-result">
                              <div className="winner">
                                <span className="winner-name">{winnerName}</span>
                                <span className="winner-label">{t('tournaments.matches.advancesToNextRound')}</span>
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="player">
                              <span className="player-name">
                                {match.player_1_name}
                              </span>
                            </div>
                            <div className="vs">vs</div>
                            <div className="player">
                              <span className="player-name">
                                {match.player_2_name}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="match-actions">
                      {isPending ? (
                        <div className="match-status pending-status">
                          ⏳ {t('tournaments.matches.waitingForPlayers')}
                        </div>
                      ) : isWalkover ? (
                        <div className="match-status walkover-status">
                          ✓ {t('tournaments.matches.completed')}
                        </div>
                      ) : isCompleted ? (
                        <div className="match-status completed-status">
                          ✓ {t('tournaments.matches.completed')}
                        </div>
                      ) : isInProgress ? (
                        <>
                          <div className="match-status in-progress-status">
                            ▶ {t('tournaments.matches.state.in_progress')}
                          </div>
                          <button
                            className="btn btn-secondary btn-small"
                            onClick={() => handleResetMatch(match.match_id)}
                            disabled={resettingMatch === match.match_id}
                            title={t('tournaments.matches.resetTooltip')}
                          >
                            {resettingMatch === match.match_id 
                              ? t('common.processing')
                              : '🔄 ' + t('tournaments.matches.reset')
                            }
                          </button>
                        </>
                      ) : isReady ? (
                        <button
                          className="btn btn-primary"
                          onClick={() => handleStartMatch(match.match_id)}
                          disabled={startingMatch === match.match_id}
                          title={t('tournaments.matches.startGameTooltip')}
                        >
                          {startingMatch === match.match_id 
                            ? t('common.processing')
                            : t('tournaments.matches.startGame')
                          }
                        </button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          TABLE ASSIGNMENT MODAL
          ══════════════════════════════════════════════════════════════════════ */}
      {showTableModal && (
        <div className="modal-overlay" style={{ zIndex: 1001 }}>
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>{t('tableDevices.assignTable')}</h3>
              <button onClick={handleSkipTableAssignment} className="close-button">
                &times;
              </button>
            </div>

            <div className="modal-body">
              <p style={{ marginBottom: '16px' }}>
                {t('tableDevices.assignTableHint')}
              </p>

              {availableTables.length === 0 ? (
                <p style={{ color: '#666', fontStyle: 'italic' }}>
                  {t('tableDevices.noTablesAvailable')}
                </p>
              ) : (
                <div className="form-group">
                  <label htmlFor="table-select">
                    {t('tableDevices.selectTable')}:
                  </label>
                  <select
                    id="table-select"
                    value={selectedTableId || ''}
                    onChange={(e) => setSelectedTableId(Number(e.target.value))}
                    className="form-control"
                  >
                    <option value="">{t('common.pleaseSelect')}</option>
                    {availableTables.map((table) => (
                      <option key={table.id} value={table.id}>
                        {table.table_name}
                        {table.location && ` (${table.location})`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="modal-actions" style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
                <button
                  onClick={handleAssignTable}
                  disabled={!selectedTableId || assigningTable}
                  className="btn btn-primary"
                  style={{ flex: 1 }}
                >
                  {assigningTable ? t('common.processing') : t('common.assign')}
                </button>
                <button
                  onClick={handleSkipTableAssignment}
                  className="btn btn-secondary"
                  style={{ flex: 1 }}
                >
                  {t('common.skip')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TournamentMatchManagementV2;
